import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Filter, Phone, Play, Linkedin, Globe, FileText, MessageSquare, X, AlertCircle, Users, Calendar, TrendingUp, PhoneCall, CheckCircle, Pause, Lock, Snowflake, Flame, Sun, Clock, CalendarClock, XOctagon, MessageCircle, CheckCircle2, Send, Inbox, UserCheck } from 'lucide-react';
import PaginationBar from '../shared/PaginationBar';
import { getCampaignLeadStatusTag } from '../../utils/leadStatusBadge';
import { formatInTz } from '../../utils/dateFormatters';
import { useTimezone } from '../../hooks/useTimezone';
import { useCallCampaignLeads } from '../../hooks/useSupabaseLeads';
import { supabase } from '../../lib/supabase';
import StatsCard from '../Dashboard/StatsCard';
import CallRecordingsModal from '../CallAgent/CallRecordingsModal';
import CallTranscriptsModal from '../CallAgent/CallTranscriptsModal';
import SimpleCustomSelect from '../shared/SimpleCustomSelect';
import LoadingScreen from '../shared/LoadingScreen';

const CallCampaignPage: React.FC = () => {
  const tz = useTimezone();
  const navigate = useNavigate();
  const { data: allLeads, isLoading, error } = useCallCampaignLeads();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [temperatureFilter, setTemperatureFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [selectedLead, setSelectedLead] = useState<any>(null);
  const [showCallRecordingsModal, setShowCallRecordingsModal] = useState(false);
  const [selectedLeadForRecordings, setSelectedLeadForRecordings] = useState<any>(null);
  const [showCallTranscriptsModal, setShowCallTranscriptsModal] = useState(false);
  const [selectedLeadForTranscripts, setSelectedLeadForTranscripts] = useState<any>(null);
  const [selectedLeadIds, setSelectedLeadIds] = useState<string[]>([]);
  const [selectAllLeads, setSelectAllLeads] = useState(false);
  const [activeBulkAction, setActiveBulkAction] = useState<string | null>(null);
  const itemsPerPage = 10;

  const filteredLeads = useMemo(() => {
    return allLeads.filter(lead => {
      const matchesSearch = (lead.company_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (lead.dm_full_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (lead.lead_id || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (lead.dm_mobile_1 || '').includes(searchTerm);
      const matchesStatus = statusFilter === 'all' || lead.lead_status === statusFilter;
      const matchesTemperature = temperatureFilter === 'all' ||
                                (lead.lead_temprature || '').trim().toLowerCase().includes(temperatureFilter.toLowerCase());
      return matchesSearch && matchesStatus && matchesTemperature;
    });
  }, [allLeads, searchTerm, statusFilter, temperatureFilter]);

  const totalPages = Math.ceil(filteredLeads.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const callLeads = useMemo(() => {
    return filteredLeads.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredLeads, startIndex, itemsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
    setSelectedLeadIds([]);
    setSelectAllLeads(false);
  }, [searchTerm, statusFilter, temperatureFilter]);

  const handleSelectAll = () => {
    const allFilteredIds = callLeads.map(lead => lead.lead_id).filter(Boolean);
    if (selectedLeadIds.length === allFilteredIds.length && allFilteredIds.length > 0) {
      setSelectedLeadIds([]);
      setSelectAllLeads(false);
    } else {
      setSelectedLeadIds(allFilteredIds);
      setSelectAllLeads(false);
    }
  };

  const handleSelectAllLeads = () => {
    const allFilteredIds = filteredLeads.map(lead => lead.lead_id).filter(Boolean);
    setSelectedLeadIds(allFilteredIds);
    setSelectAllLeads(true);
  };

  const handleSelectLead = (lead_id: string) => {
    if (!lead_id) return;

    setSelectedLeadIds(prev => {
      if (prev.includes(lead_id)) {
        setSelectAllLeads(false);
        return prev.filter(leadId => leadId !== lead_id);
      } else {
        return [...prev, lead_id];
      }
    });
  };

  const allCurrentPageSelected = useMemo(() => {
    const allPageIds = callLeads.map(lead => lead.lead_id).filter(Boolean);
    return allPageIds.length > 0 && allPageIds.every(id => selectedLeadIds.includes(id));
  }, [callLeads, selectedLeadIds]);

  const showSelectAllBanner = allCurrentPageSelected && !selectAllLeads && filteredLeads.length > callLeads.length;

  const handleBulkStatusChange = async (status: string) => {
    if (selectedLeadIds.length === 0) return;

    setActiveBulkAction(status);

    try {
      const leadsToProcess = selectAllLeads
        ? filteredLeads.map(lead => lead.lead_id).filter(Boolean)
        : selectedLeadIds;

      if (status === 'on hold') {
        await supabase.from('leads_tracking')
          .update({ ca_lead_status: 'on hold', updated_at: new Date().toISOString() })
          .in('lead_id', leadsToProcess);
        await supabase.from('leads_campaigns')
          .update({ status: 'on hold', updated_at: new Date().toISOString() })
          .in('lead_id', leadsToProcess)
          .eq('campaign_type', 'call');
      } else if (status === 'active') {
        const { data: lcRows } = await supabase.from('leads_campaigns')
          .select('lead_id, campaign_step')
          .in('lead_id', leadsToProcess)
          .eq('campaign_type', 'call');
        const step0Ids = (lcRows || []).filter(r => (r.campaign_step || 0) === 0).map(r => r.lead_id);
        const stepGte1Ids = (lcRows || []).filter(r => (r.campaign_step || 0) >= 1).map(r => r.lead_id);
        if (step0Ids.length > 0) {
          await supabase.from('leads_campaigns')
            .update({ status: 'assigned', updated_at: new Date().toISOString() })
            .in('lead_id', step0Ids)
            .eq('campaign_type', 'call');
          await supabase.from('leads_tracking')
            .update({ ca_lead_status: 'assigned', updated_at: new Date().toISOString() })
            .in('lead_id', step0Ids);
        }
        if (stepGte1Ids.length > 0) {
          await supabase.from('leads_campaigns')
            .update({ status: 'follow up', updated_at: new Date().toISOString() })
            .in('lead_id', stepGte1Ids)
            .eq('campaign_type', 'call');
          await supabase.from('leads_tracking')
            .update({ ca_lead_status: 'follow up', updated_at: new Date().toISOString() })
            .in('lead_id', stepGte1Ids);
        }
      } else if (status === 'closed') {
        await supabase.from('leads_tracking')
          .update({ ca_lead_status: 'closed', updated_at: new Date().toISOString() })
          .in('lead_id', leadsToProcess);
        await supabase.from('leads_campaigns')
          .update({ status: 'completed', updated_at: new Date().toISOString() })
          .in('lead_id', leadsToProcess)
          .eq('campaign_type', 'call');
      }

      setSelectedLeadIds([]);
      setSelectAllLeads(false);
    } catch (error) {
      console.error('Failed to update lead status:', error);
    } finally {
      setActiveBulkAction(null);
    }
  };

  const stats = useMemo(() => {
    const leadsContacted = allLeads.filter(lead => (parseInt(lead.call_attempts) || 0) > 0).length;
    const totalCalls = allLeads.reduce((sum, lead) => sum + (parseInt(lead.call_attempts) || 0), 0);
    const connectedCalls = allLeads.reduce((sum, lead) => sum + (parseInt(lead.pickups) || 0), 0);
    const answerRate = totalCalls > 0 ? Math.round((connectedCalls / totalCalls) * 100) : 0;
    const engagedLeads = allLeads.filter(lead => {
      const temperature = (lead.lead_temprature || '').toLowerCase();
      return temperature.includes('hot') || temperature.includes('warm');
    }).length;
    const bookedDemos = allLeads.filter(lead => {
      const status = (lead.lead_status || '').toLowerCase();
      return status.includes('booked');
    }).length;

    return { leadsContacted, totalCalls, connectedCalls, answerRate, engagedLeads, bookedDemos };
  }, [allLeads]);

  const getLeadStatusTag = getCampaignLeadStatusTag;

  const getOutcomeTag = (outcome: string) => {
    if (!outcome || outcome.trim() === '' || outcome.toLowerCase() === 'n/a') return null;
    const configs: Record<string, { label: string; bg: string; text: string; border: string; dot: string }> = {
      'connected': { label: 'Connected', bg: 'bg-emerald-500/10', text: 'text-emerald-700', border: 'border-emerald-500/20', dot: 'bg-emerald-500' },
      'not connected': { label: 'Not Connected', bg: 'bg-rose-500/10', text: 'text-rose-700', border: 'border-rose-500/20', dot: 'bg-rose-500' },
      'call later': { label: 'Call Later', bg: 'bg-amber-500/10', text: 'text-amber-700', border: 'border-amber-500/20', dot: 'bg-amber-500' },
      'info requested': { label: 'Info Requested', bg: 'bg-sky-500/10', text: 'text-sky-700', border: 'border-sky-500/20', dot: 'bg-sky-500' },
      'not interested': { label: 'Not Interested', bg: 'bg-gray-500/10', text: 'text-gray-600', border: 'border-gray-400/20', dot: 'bg-gray-400' },
      'wrong person': { label: 'Wrong Person', bg: 'bg-orange-500/10', text: 'text-orange-700', border: 'border-orange-500/20', dot: 'bg-orange-500' },
      'do not call': { label: 'Do Not Call', bg: 'bg-red-600/10', text: 'text-red-700', border: 'border-red-600/20', dot: 'bg-red-600' },
      'booked': { label: 'Booked', bg: 'bg-[#05E0D1]/10', text: 'text-[#0bb5a5]', border: 'border-[#05E0D1]/20', dot: 'bg-[#05E0D1]' },
      'failed': { label: 'Failed', bg: 'bg-gray-500/10', text: 'text-gray-500', border: 'border-gray-400/20', dot: 'bg-gray-400' },
    };
    const config = configs[outcome?.toLowerCase()] || { label: outcome, bg: 'bg-gray-50', text: 'text-gray-600', border: 'border-gray-200', dot: 'bg-gray-400' };
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold ${config.bg} ${config.text} border ${config.border} shadow-sm`}>
        <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`}></span>
        {config.label}
      </span>
    );
  };

  const getTemperatureTag = (temperature: string) => {
    if (!temperature || temperature.trim() === '' || temperature.toLowerCase() === 'n/a') return null;
    const tempLower = temperature?.toLowerCase();
    if (tempLower?.includes('cold')) return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold bg-[#1875FF]/8 text-[#1875FF] border border-[#1875FF]/15 shadow-sm">
        <Snowflake size={12} />
        Cold
      </span>
    );
    if (tempLower?.includes('warm')) return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold bg-amber-500/8 text-amber-600 border border-amber-500/15 shadow-sm">
        <Sun size={12} />
        Warm
      </span>
    );
    if (tempLower?.includes('hot')) return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold bg-gradient-to-r from-red-500/10 to-orange-500/10 text-red-600 border border-red-400/20 shadow-sm shadow-red-500/10">
        <Flame size={12} className="text-red-500" />
        Hot
      </span>
    );
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold bg-gray-50 text-gray-600 border border-gray-200">
        {temperature}
      </span>
    );
  };

  const handleOpenCallTranscriptsModal = (lead: any) => {
    setSelectedLeadForTranscripts(lead);
    setShowCallTranscriptsModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setModalTitle('');
    setSelectedLead(null);
    setShowCallTranscriptsModal(false);
    setSelectedLeadForTranscripts(null);
  };

  const handleOpenCallRecordingsModal = (lead: any) => {
    setSelectedLeadForRecordings(lead);
    setShowCallRecordingsModal(true);
  };

  const handleCloseCallRecordingsModal = () => {
    setShowCallRecordingsModal(false);
    setSelectedLeadForRecordings(null);
  };

  const handleOpenModal = (title: string, leadData?: any) => {
    setModalTitle(title);
    setSelectedLead(leadData || null);
    setShowModal(true);
  };

  if (isLoading && allLeads.length === 0) {
    return <LoadingScreen />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/campaigns')}
          className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft size={20} className="mr-1" />
          Back to Campaigns
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border-2 border-red-200 text-red-800 p-4 rounded-xl flex items-center space-x-2 mb-6 shadow-sm">
          <AlertCircle size={20} />
          <span className="font-medium">{error}</span>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <StatsCard title="Leads Contacted" value={stats.leadsContacted} icon={UserCheck} color="blue" compact />
        <StatsCard title="Calls" value={stats.totalCalls} icon={Phone} color="blue" compact />
        <StatsCard title="Connected Calls" value={stats.connectedCalls} icon={PhoneCall} color="green" compact />
        <StatsCard title="Answer Rate" value={`${stats.answerRate}%`} icon={TrendingUp} color="orange" compact />
        <StatsCard title="Engaged Leads" value={stats.engagedLeads} icon={Users} color="blue" compact />
        <StatsCard title="Booked Meetings" value={stats.bookedDemos} icon={Calendar} color="green" compact />
      </div>

      <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 p-5 shadow-lg">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={20} />
              <input
                type="text"
                placeholder="Search leads..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#1875FF] focus:border-[#1875FF] transition-all font-medium"
              />
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Filter size={16} className="text-gray-500" />
              <SimpleCustomSelect
                value={statusFilter}
                onChange={setStatusFilter}
                options={[
                  { value: 'all', label: 'All Status' },
                  { value: 'assigned', label: 'Assigned' },
                  { value: 'queued', label: 'Queued' },
                  { value: 'calling', label: 'Calling' },
                  { value: 'booked', label: 'Booked' },
                  { value: 'follow up', label: 'Follow Up' },
                  { value: 'on hold', label: 'On Hold' },
                  { value: 'closed', label: 'Closed' }
                ]}
                placeholder="All Status"
                className="w-48"
              />
              <SimpleCustomSelect
                value={temperatureFilter}
                onChange={setTemperatureFilter}
                options={[
                  { value: 'all', label: 'All Temperature' },
                  { value: 'cold', label: '❄️ Cold' },
                  { value: 'warm', label: '🌤️ Warm' },
                  { value: 'hot', label: '🔥 Hot' }
                ]}
                placeholder="All Temperature"
                className="w-48"
              />
            </div>
          </div>
        </div>
      </div>

      {selectedLeadIds.length > 0 && (
        <div className="bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 border-2 border-[#1875FF]/40 rounded-xl p-5 shadow-2xl shadow-[#1875FF]/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-[#1875FF]/20 to-[#05E0D1]/20 rounded-lg flex items-center justify-center animate-pulse shadow-md">
                <CheckCircle className="text-[#1875FF]" size={20} />
              </div>
              <div className="flex flex-col space-y-1">
                <span className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#1875FF] to-[#05E0D1]">
                  {selectAllLeads ? `All ${filteredLeads.length}` : selectedLeadIds.length} lead{(selectAllLeads ? filteredLeads.length : selectedLeadIds.length) > 1 ? 's' : ''} selected
                </span>
                {showSelectAllBanner && (
                  <button
                    onClick={handleSelectAllLeads}
                    className="text-[#1875FF] font-semibold hover:underline focus:outline-none text-sm text-left"
                  >
                    Select all {filteredLeads.length} leads
                  </button>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleBulkStatusChange('active')}
                disabled={activeBulkAction !== null}
                className="flex items-center space-x-2 px-4 py-2.5 bg-gradient-to-r from-[#1875FF] to-[#05E0D1] text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-[#1875FF]/40 hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                {activeBulkAction === 'active' ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Updating...</span>
                  </>
                ) : (
                  <>
                    <Play size={18} />
                    <span>Activate</span>
                  </>
                )}
              </button>
              <button
                onClick={() => handleBulkStatusChange('on hold')}
                disabled={activeBulkAction !== null}
                className="flex items-center space-x-2 px-4 py-2.5 bg-gradient-to-r from-[#05E0D1] to-[#1875FF] text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-[#05E0D1]/40 hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                {activeBulkAction === 'on hold' ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Updating...</span>
                  </>
                ) : (
                  <>
                    <Pause size={18} />
                    <span>On Hold</span>
                  </>
                )}
              </button>
              <button
                onClick={() => handleBulkStatusChange('closed')}
                disabled={activeBulkAction !== null}
                className="flex items-center space-x-2 px-4 py-2.5 bg-gradient-to-r from-[#1875FF] to-[#05E0D1] text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-[#1875FF]/40 hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                {activeBulkAction === 'closed' ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Updating...</span>
                  </>
                ) : (
                  <>
                    <Lock size={18} />
                    <span>Close</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 shadow-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full table-fixed">
            <thead className="bg-gradient-to-r from-[#05E0D1]/10 to-[#1875FF]/10 border-b-2 border-[#1875FF]/10">
              <tr>
                <th className="text-center px-2 py-4 w-12">
                  <div className="flex items-center justify-center">
                    <input
                      type="checkbox"
                      checked={callLeads.length > 0 && selectedLeadIds.length === callLeads.map(lead => lead.lead_id).filter(Boolean).length}
                      ref={(el) => {
                        if (el) {
                          const allFilteredIds = callLeads.map(lead => lead.lead_id).filter(Boolean);
                          el.indeterminate = selectedLeadIds.length > 0 && selectedLeadIds.length < allFilteredIds.length;
                        }
                      }}
                      onChange={handleSelectAll}
                      className="custom-checkbox w-3.5 h-3.5 rounded border border-gray-300 bg-white hover:border-[#1875FF]/50 focus:ring-0 transition-all duration-200 cursor-pointer checked:bg-gradient-to-r checked:from-[#1875FF] checked:to-[#05E0D1] checked:border-transparent"
                    />
                  </div>
                </th>
                <th className="text-left px-2 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans w-[150px]">Person</th>
                <th className="text-left px-2 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans w-[150px]">Company</th>
                <th className="text-center px-2 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans w-[100px]">
                  <div>Calls</div>
                  <div className="font-normal text-[10px] text-gray-500">Attempts / Pickups</div>
                </th>
                <th className="text-center px-2 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans w-[130px]">Last Call Outcome</th>
                <th className="text-center px-2 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans w-[100px]">Next Call</th>
                <th className="text-center px-2 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans w-[100px]">Temperature</th>
                <th className="text-center px-2 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans w-[100px]">Lead Status</th>
                <th className="text-center px-2 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans w-[100px]">Convo Record</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1875FF]/10">
              {callLeads.map((lead) => (
                <tr key={lead.lead_id || lead.id} className="hover:bg-[#1875FF]/5 transition-colors">
                  <td className="px-2 py-4">
                    <div className="flex items-center justify-center">
                      <input
                        type="checkbox"
                        checked={lead.lead_id ? selectedLeadIds.includes(lead.lead_id) : false}
                        onChange={() => handleSelectLead(lead.lead_id)}
                        className="custom-checkbox w-3.5 h-3.5 rounded border border-gray-300 bg-white hover:border-[#1875FF]/50 focus:ring-0 transition-all duration-200 cursor-pointer checked:bg-gradient-to-r checked:from-[#1875FF] checked:to-[#05E0D1] checked:border-transparent"
                      />
                    </div>
                  </td>
                  <td className="px-2 py-4 w-[150px]">
                    <div className="flex items-center space-x-1 mb-1">
                      <span className="text-sm font-medium text-gray-900 break-words whitespace-normal">{lead.dm_full_name || 'N/A'}</span>
                      {lead.dm_linkedin && (
                        <a href={lead.dm_linkedin.startsWith('http') ? lead.dm_linkedin : `https://${lead.dm_linkedin}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 transition-colors" title="LinkedIn Profile">
                          <Linkedin size={12} />
                        </a>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 break-words whitespace-normal">{lead.dm_title || 'N/A'}</div>
                    {lead.dm_mobile_1 && <div className="text-xs text-gray-600 break-words">{lead.dm_mobile_1}</div>}
                    {lead.dm_mobile_2 && <div className="text-xs text-gray-600 break-words">{lead.dm_mobile_2}</div>}
                  </td>
                  <td className="px-2 py-4 w-[150px]">
                    <div className="text-sm font-medium text-gray-900 break-words whitespace-normal mb-2">{lead.company_name || 'N/A'}</div>
                    <div className="flex items-center space-x-2">
                      {lead.company_website && (
                        <a href={lead.company_website.startsWith('http') ? lead.company_website : `https://${lead.company_website}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 transition-colors" title="Visit Website">
                          <Globe size={14} />
                        </a>
                      )}
                      {lead.company_linkedin && (
                        <a href={lead.company_linkedin.startsWith('http') ? lead.company_linkedin : `https://${lead.company_linkedin}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 transition-colors" title="LinkedIn Profile">
                          <Linkedin size={14} />
                        </a>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-4 text-center w-[100px]">
                    <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-lg bg-gradient-to-r from-[#1875FF]/5 to-[#05E0D1]/5 border border-[#1875FF]/10">
                      <div className="flex items-center gap-1">
                        <Phone size={10} className="text-[#1875FF]" />
                        <span className="text-xs font-bold text-[#1875FF]">{lead.call_attempts || 0}</span>
                      </div>
                      <div className="w-px h-3 bg-gray-300"></div>
                      <div className="flex items-center gap-1">
                        <PhoneCall size={10} className="text-[#05E0D1]" />
                        <span className="text-xs font-bold text-[#0bb5a5]">{lead.pickups || 0}</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-2 py-4 text-center w-[130px]">{getOutcomeTag(lead.last_call_outcome)}</td>
                  <td className="px-2 py-4 text-center w-[100px]">
                    {lead.next_call && !['closed', 'booked'].includes((lead.lead_status || '').toLowerCase()) ? (() => {
                      const nextCallDate = new Date(lead.next_call);
                      const now = new Date();
                      const diffHours = (nextCallDate.getTime() - now.getTime()) / (1000 * 60 * 60);
                      let colorClasses = 'bg-[#05E0D1]/8 text-[#0bb5a5] border-[#05E0D1]/15';
                      if (diffHours < 0) {
                        colorClasses = 'bg-[#1875FF]/10 text-[#1875FF] border-[#1875FF]/20';
                      } else if (diffHours < 24) {
                        colorClasses = 'bg-gradient-to-r from-[#1875FF]/8 to-[#05E0D1]/8 text-[#1875FF] border-[#1875FF]/15';
                      }
                      return (
                        <div className={`inline-flex items-center whitespace-nowrap px-2.5 py-1 rounded-lg border shadow-sm ${colorClasses}`}>
                          <span className="text-xs font-semibold">{formatInTz(nextCallDate, 'MMM dd, HH:mm', tz)}</span>
                        </div>
                      );
                    })() : <span className="text-xs text-gray-400">--</span>}
                  </td>
                  <td className="px-2 py-4 text-center w-[100px]">{getTemperatureTag(lead.lead_temprature)}</td>
                  <td className="px-2 py-4 text-center w-[100px]">{getLeadStatusTag(lead.lead_status)}</td>
                  <td className="px-2 py-4 text-center w-[100px]">
                    <div className="flex items-center justify-center gap-1.5">
                      {lead.has_transcripts && (
                        <button
                          onClick={() => handleOpenCallTranscriptsModal(lead)}
                          className="group relative w-7 h-7 rounded-lg border border-[#1875FF]/15 bg-[#1875FF]/5 flex items-center justify-center hover:bg-gradient-to-r hover:from-[#1875FF] hover:to-[#05E0D1] hover:border-transparent hover:shadow-md hover:shadow-[#1875FF]/25 transition-all duration-200"
                          title="View Call Transcripts"
                        >
                          <FileText size={13} className="text-[#1875FF] group-hover:text-white transition-colors duration-200" />
                        </button>
                      )}
                      {lead.has_recordings && (
                        <button
                          onClick={() => handleOpenCallRecordingsModal(lead)}
                          className="group relative w-7 h-7 rounded-lg border border-[#05E0D1]/20 bg-[#05E0D1]/5 flex items-center justify-center hover:bg-gradient-to-r hover:from-[#05E0D1] hover:to-[#1875FF] hover:border-transparent hover:shadow-md hover:shadow-[#05E0D1]/25 transition-all duration-200"
                          title="View Call Recordings"
                        >
                          <Play size={13} className="text-[#0bb5a5] group-hover:text-white transition-colors duration-200" />
                        </button>
                      )}
                      {(lead.ovrall_calls_convo_summary || lead.calls_convo_history_summarized) && (
                        <button
                          onClick={() => handleOpenModal('Conversation History', lead)}
                          className="group relative w-7 h-7 rounded-lg border border-[#1875FF]/15 bg-[#1875FF]/5 flex items-center justify-center hover:bg-gradient-to-r hover:from-[#1875FF] hover:to-[#05E0D1] hover:border-transparent hover:shadow-md hover:shadow-[#1875FF]/25 transition-all duration-200"
                          title="View History"
                        >
                          <MessageSquare size={13} className="text-[#1875FF] group-hover:text-white transition-colors duration-200" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {filteredLeads.length === 0 && !isLoading && (
        <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 p-12 text-center shadow-xl">
          <div className="text-gray-700 text-lg font-bold">No call leads found</div>
          <div className="text-gray-600 text-sm mt-1 font-medium">
            {searchTerm || statusFilter !== 'all' || temperatureFilter !== 'all' ? 'Try adjusting your filters' : 'Call leads will appear here once they\'re added to the system'}
          </div>
        </div>
      )}

      <PaginationBar
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        totalItems={filteredLeads.length}
        startIndex={startIndex}
        itemsPerPage={itemsPerPage}
      />

      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col shadow-2xl shadow-black/20 animate-fadeIn">
            <div className="flex items-center justify-between px-6 py-5 border-b border-[#05E0D1]/20 bg-gradient-to-r from-[#1875FF]/5 to-[#05E0D1]/5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center shadow-lg">
                  <MessageSquare className="text-white" size={18} />
                </div>
                <div>
                  <h3 className="text-lg font-extrabold text-gray-900 font-display">{modalTitle}</h3>
                  <p className="text-xs text-gray-500 font-medium">
                    {selectedLead?.dm_full_name ? `History for ${selectedLead.dm_full_name}` : 'Call conversation details'}
                  </p>
                </div>
              </div>
              <button onClick={handleCloseModal} className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-white rounded-lg">
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto custom-scrollbar p-6">
              {modalTitle === 'Conversation History' && selectedLead ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  <div className="bg-[#05E0D1]/5 border border-[#05E0D1]/15 rounded-xl p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-6 h-6 bg-gradient-to-br from-[#05E0D1] to-[#1875FF] rounded-lg flex items-center justify-center">
                        <FileText className="text-white" size={10} />
                      </div>
                      <h4 className="text-sm font-bold text-gray-900">Convo Summary</h4>
                    </div>
                    <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                      <div className="text-sm text-gray-700 whitespace-pre-wrap break-words leading-relaxed">
                        {selectedLead.ovrall_calls_convo_summary || 'No conversation summary available'}
                      </div>
                    </div>
                  </div>
                  <div className="bg-[#1875FF]/5 border border-[#1875FF]/15 rounded-xl p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-6 h-6 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-lg flex items-center justify-center">
                        <Phone className="text-white" size={10} />
                      </div>
                      <h4 className="text-sm font-bold text-gray-900">Calls History Overview</h4>
                    </div>
                    <div className="space-y-3">
                      {selectedLead.calls_convo_history_summarized ? (
                        (() => {
                          const callsHistory = selectedLead.calls_convo_history_summarized.replace(/\\n/g, '\n');
                          const callBlocks = callsHistory.split(/(?=\d+(?:st|nd|rd|th)\s+Call)/i).filter((block: string) => block.trim());

                          return callBlocks.map((block: string, index: number) => {
                            const lines = block.trim().split('\n').filter((line: string) => line.trim());
                            if (lines.length === 0) return null;

                            const headerLine = lines[0];
                            const descriptionLines = lines.slice(1);

                            return (
                              <div key={index} className="relative pl-7 pb-3">
                                <div className="absolute left-0 top-1 w-4 h-4 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-md flex items-center justify-center shadow-sm">
                                  <Phone className="text-white" size={8} />
                                </div>
                                {index < callBlocks.length - 1 && <div className="absolute left-2 top-5 w-0.5 h-full bg-[#1875FF]/15"></div>}
                                <div className="text-sm font-semibold text-gray-900 mb-0.5">{headerLine}</div>
                                <div className="text-xs text-gray-600 leading-relaxed">{descriptionLines.join(' ')}</div>
                              </div>
                            );
                          });
                        })()
                      ) : (
                        <div className="text-center py-8">
                          <div className="w-10 h-10 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-xl flex items-center justify-center mx-auto mb-2">
                            <Phone size={18} className="text-[#1875FF]" />
                          </div>
                          <p className="text-gray-400 text-xs font-medium">No calls history available</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-16">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <MessageSquare size={24} className="text-[#1875FF]" />
                  </div>
                  <p className="text-gray-400 text-sm font-medium">No content available</p>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {showCallRecordingsModal && selectedLeadForRecordings && (
        <CallRecordingsModal
          leadId={selectedLeadForRecordings.lead_id}
          leadName={selectedLeadForRecordings.dm_full_name || 'Unknown Lead'}
          onClose={handleCloseCallRecordingsModal}
        />
      )}

      {showCallTranscriptsModal && selectedLeadForTranscripts && (
        <CallTranscriptsModal
          leadId={selectedLeadForTranscripts.lead_id}
          leadName={selectedLeadForTranscripts.dm_full_name || 'Unknown Lead'}
          onClose={() => {
            setShowCallTranscriptsModal(false);
            setSelectedLeadForTranscripts(null);
          }}
        />
      )}
    </div>
  );
};

export default CallCampaignPage;
